import { useMutation, useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "../api";
import type { Dayjs } from "dayjs";

// --------------------------------------------------------------------------
// Types
// --------------------------------------------------------------------------

export interface EshopSyncRun {
  id: number;
  started_at: Dayjs;
  finished_at: Dayjs | null;
  orders_seen: number;
  orders_created: number;
  orders_updated: number;
  orders_skipped: number;
  requests_made: number;
  last_seen_created_at: Dayjs | null;
  error: string | null;
}

interface EshopSyncRunsPage {
  items: EshopSyncRun[];
  next_cursor: string | null;
  total_estimate: number;
}

export interface EshopSyncStatus {
  running: boolean;
  last_run: EshopSyncRun | null;
}

// --------------------------------------------------------------------------
// Queries
// --------------------------------------------------------------------------

export function useEshopSyncRuns() {
  return useInfiniteQuery<EshopSyncRunsPage>({
    queryKey: ["eshop", "sync", "runs"],
    queryFn: ({ pageParam }) =>
      apiGet(`/eshop/sync/runs${pageParam ? `?cursor=${pageParam}` : ""}`),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    initialPageParam: undefined,
  });
}

/** Poll the sync status endpoint. Pass a positive refetchInterval (ms) to
 *  enable polling while a sync is running. */
export function useEshopSyncStatus(refetchInterval?: number) {
  return useQuery<EshopSyncStatus>({
    queryKey: ["eshop", "sync", "status"],
    queryFn: () => apiGet("/eshop/sync/status"),
    refetchInterval,
  });
}

// --------------------------------------------------------------------------
// Mutations
// --------------------------------------------------------------------------

export function useTriggerEshopSync() {
  const qc = useQueryClient();
  return useMutation<void, unknown, void>({
    mutationFn: () => apiPost("/eshop/sync", {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["eshop", "sync", "status"] });
      qc.invalidateQueries({ queryKey: ["eshop", "sync", "runs"] });
    },
  });
}

/** Trigger a one-off backfill import from a specific date (superadmin only). */
export function useTriggerBackfill() {
  const qc = useQueryClient();
  return useMutation<void, unknown, { from: string }>({
    mutationFn: ({ from }) => apiPost("/eshop/sync/backfill", { from }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["eshop", "sync", "status"] });
      qc.invalidateQueries({ queryKey: ["eshop", "sync", "runs"] });
    },
  });
}
